import React, { useState, useEffect } from 'react';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { Order, Refund } from '../types';
import Modal from 'react-modal';
import styles from './AddRefundModal.module.css';
import AsyncSelect from 'react-select/async';
import { loadOrderOptions } from '../util/loadOrderOptions';
import { toast } from 'react-toastify';
import updateNumberValue from '../util/updateNumberValue';
import ConfirmationModal from './ConfirmationModal';

interface AddRefundModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  orders: Order[];
  disableOrderSelect?: boolean;
  defaultValue?: Refund | null;
  selectedOrder?: { label: string; value: number } | null;
}

const today = new Date().toISOString().split('T')[0];

const AddRefundModal: React.FC<AddRefundModalProps> = ({ isOpen, onClose, onSuccess, orders, disableOrderSelect, defaultValue, selectedOrder: propSelectedOrder }) => {
  const [selectedOrder, setSelectedOrder] = useState<{ label: string; value: number } | null>(null);
  const [amount, setAmount] = useState('0.0');
  const [description, setDescription] = useState('');
  const [refundedAt, setRefundedAt] = useState('');
  const [refundType, setRefundType] = useState<'cash' | 'credit'>('cash');
  const [showCreditConfirm, setShowCreditConfirm] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (!isOpen) {
      resetFields();
    } else if (defaultValue) {
      setSelectedOrder({ value: defaultValue.order_id, label: defaultValue.order.order_reference });
      setAmount(String(defaultValue.amount));
      setDescription(defaultValue.description || '');
      setRefundedAt(defaultValue.refunded_at || '');
      setRefundType(defaultValue.refund_type || 'cash');
    } else {
      resetFields();
      if (propSelectedOrder) {
        setSelectedOrder(propSelectedOrder);
      } else if (disableOrderSelect && orders.length === 1) {
        const order = orders[0];
        setSelectedOrder({ value: order.id, label: order.identifier });
      }
    }
  }, [isOpen, defaultValue, orders, disableOrderSelect, propSelectedOrder]);

  const resetFields = () => {
    setSelectedOrder(null);
    setAmount('0.0');
    setDescription('');
    setRefundedAt('');
    setRefundType('cash');
    setShowCreditConfirm(false);
    setErrors({});
  };

  const validate = () => {
    const errs: { [key: string]: string } = {};
    if (!selectedOrder?.value) errs.orderId = 'Order is required';
    if (!amount) errs.amount = 'Amount is required';
    if (!description) errs.description = 'Description is required';
    return errs;
  };

  const handleSubmit = () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (!defaultValue && refundType === 'credit') {
      setShowCreditConfirm(true);
      return;
    }

    submitRefund();
  };

  const submitRefund = async () => {
    const payload = {
      order_id: selectedOrder?.value,
      amount: amount,
      description,
      refunded_at: refundedAt,
      refund_type: refundType
    };

    try {
      const res = await fetchWithAuth(
        defaultValue
          ? `/refunds/${defaultValue.id}`
          : '/refunds',
        {
          method: defaultValue ? 'PATCH' : 'POST',
          body: JSON.stringify(payload)
        }
      );

      if (res.ok) {
        resetFields();
        onSuccess();
        toast.success(defaultValue ? 'Refund updated successfully' : 'Refund added successfully');
      } else {
        toast.error('Failed to save refund');
      }
    } catch (error) {
      toast.error('An unexpected error occurred');
    }
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} className={styles.modalContent}>
      <h2 className="text-lg font-semibold mb-4">{defaultValue ? 'Edit Refund' : 'Add Refund'}</h2>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Order</label>
          <AsyncSelect
            cacheOptions
            defaultOptions
            loadOptions={loadOrderOptions}
            value={selectedOrder}
            onChange={(selectedOption) => setSelectedOrder(selectedOption)}
            isDisabled={disableOrderSelect}
            className="mt-1"
          />
          {errors.orderId && <p className="text-red-500 text-sm">{errors.orderId}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Type</label>
          {defaultValue ? (
            <span className={`mt-1 inline-block px-2 py-0.5 text-xs font-medium rounded-full ${
              refundType === 'credit'
                ? 'bg-blue-100 text-blue-800'
                : 'bg-gray-100 text-gray-800'
            }`}>
              {refundType === 'credit' ? 'Credit' : 'Cash'}
            </span>
          ) : (
            <div className="flex gap-4 mt-1">
              {(['cash', 'credit'] as const).map((type) => (
                <label key={type} className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="refundType"
                    value={type}
                    checked={refundType === type}
                    onChange={() => {
                      setRefundType(type);
                      if (type === 'credit') setRefundedAt(today);
                      else setRefundedAt('');
                    }}
                  />
                  <span className="text-sm capitalize">{type}</span>
                </label>
              ))}
            </div>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Amount</label>
          <input
            type="text"
            inputMode='decimal'
            value={amount}
            onChange={e => updateNumberValue(e.target.value, setAmount)}
            className="w-full border rounded px-3 py-2 mt-1"
          />
          {errors.amount && <p className="text-red-500 text-sm">{errors.amount}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Description</label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            className="w-full border rounded px-3 py-2 mt-1"
          />
          {errors.description && <p className="text-red-500 text-sm">{errors.description}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Refunded At</label>
          <input
            type="date"
            value={refundedAt}
            onChange={e => setRefundedAt(e.target.value)}
            disabled={refundType === 'credit'}
            className={`w-full border rounded px-3 py-2 mt-1 ${refundType === 'credit' ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
          />
          {errors.refundedAt && <p className="text-red-500 text-sm">{errors.refundedAt}</p>}
        </div>
        <div className="flex justify-end space-x-2 mt-4">
          <button
            className="bg-gray-300 px-4 py-2 rounded hover:bg-gray-400"
            onClick={() => {
              resetFields();
              onClose();
            }}
          >
            Cancel
          </button>
          <button
            className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800"
            onClick={handleSubmit}
          >
            Save
          </button>
        </div>
      </div>
      <ConfirmationModal
        isOpen={showCreditConfirm}
        message={`Are you sure you'd like to add credit of E${parseFloat(amount || '0').toFixed(2)} to the customer's account?`}
        onConfirm={() => {
          setShowCreditConfirm(false);
          submitRefund();
        }}
        onCancel={() => setShowCreditConfirm(false)}
      />
    </Modal>
  );
};

export default AddRefundModal;
